import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { GenericColumn, isDateColumn } from '../KanbanBoard/utils/tableColumns';
import FilterPopover from '../KanbanBoard/components/FilterPopover';
import FilterTags from '../KanbanBoard/components/FilterTags';
import { useTableFilters } from '../KanbanBoard/hooks/useTableFilters';
import { deepEqual } from '../utils';
import PaginationControls from '../MoldList/PaginationControls';
import { SavePreferenceSettings } from '../../types/tableFilterSettings';

interface ExternalPaginationProps {
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

// Server mode (mold-order-list-v2 plan §5.5): the caller already sorted `data` server-side
// for the current sortBy/sortDir. Header sort clicks call `onChange` instead of the internal
// useTableFilters sort, and the chevron/active-column highlight reflects `key`/`dir` instead of
// useTableFilters' own sortConfig.
interface ServerSortProps {
  key: string;
  dir: 'asc' | 'desc';
  onChange: (key: string, dir: 'asc' | 'desc') => void;
}

interface TableComponentProps<T> {
  data: T[];
  columns: GenericColumn<T>[];
  visibleColumns?: Record<string, boolean>;
  onRowClick?: (row: T, event?: React.MouseEvent<HTMLTableRowElement>) => void;
  onRowSelect?: (row: T) => void;
  getRowHref?: (row: T) => string;
  onDataChange?: (data: T[]) => void;
  selectableRows?: boolean;
  selectedRows?: (row: T) => boolean;
  disableRow?: (row: T) => boolean;
  onSelectAllRows?: (selected: boolean) => void;
  DisabledRowComponent?: React.FC<{ row: T }>;
  // Pagination props
  pagination?: boolean;
  initialPageSize?: number;
  pageSizeOptions?: number[];
  // External pagination control
  externalPagination?: ExternalPaginationProps;
  // Server mode: total row count across ALL pages (server mode's `data` is just the current
  // page). When set, this — not sortedItems.length — drives totalItems/totalPages.
  totalItemsOverride?: number;
  // Server mode: delegate sort clicks to the caller instead of useTableFilters' client sort.
  serverSort?: ServerSortProps;
  defaultSortKey?: string;
  savePreferenceSettings: SavePreferenceSettings;
  // Row density. 'comfortable' (default) keeps the original padding so existing tables are
  // unchanged; 'compact' tightens rows for high-row-count data grids (e.g. the v2 list).
  density?: 'comfortable' | 'compact';
  // In-place loading: when true and rows are already shown, dim the body instead of
  // blank-swapping (used by server-mode refetches on page/filter change).
  loading?: boolean;
  // Custom empty state (e.g. a filter-aware "no results" with a clear-filters action).
  // Falls back to the generic "no data" message when omitted.
  emptyState?: React.ReactNode;
  // Opt-in "fit-to-viewport": on lg+ the component becomes a flex column that fills its parent's
  // height, the table body scrolls internally (sticky thead stays pinned), and the pager stays
  // pinned at the bottom. Requires a height-bounded parent (see MainContainer fitToViewport).
  // Default false — every other table is unchanged.
  fillHeight?: boolean;
}

const TableComponent = <T extends { id: string | number }>({
  data,
  columns,
  visibleColumns,
  onRowClick,
  onRowSelect,
  getRowHref,
  onDataChange,
  selectableRows = false,
  selectedRows = () => false,
  disableRow = () => false,
  onSelectAllRows,
  DisabledRowComponent,
  // Pagination props
  pagination = false,
  initialPageSize = 10,
  pageSizeOptions = [5, 10, 25, 50, 100],
  // External pagination
  externalPagination,
  totalItemsOverride,
  serverSort,
  defaultSortKey = 'createdAt',
  savePreferenceSettings,
  density = 'comfortable',
  loading = false,
  emptyState,
  fillHeight = false,
}: TableComponentProps<T>) => {
  // Density-driven cell padding. Header keeps a slightly smaller vertical pad than body.
  const headerPad = density === 'compact' ? 'px-4 py-2' : 'px-6 py-3';
  const cellPad = density === 'compact' ? 'px-4 py-2' : 'px-6 py-4';
  const {
    sortedOrders: sortedItems,
    filterState,
    dateRangeFilters,
    activeFilter,
    filterPopoverRef,
    sortConfig,
    setActiveFilter,
    handleFilterChange,
    handleDateRangeFilterChange,
    clearDateRangeFilter,
    applyPresetDateRange,
    handleClearAllFilters,
    handleSort,
  } = useTableFilters(
    data,
    columns,
    defaultSortKey,
    savePreferenceSettings
  );

  // Use either external or internal pagination state
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const [internalPageSize, setInternalPageSize] = useState(initialPageSize);

  // Use either external or internal pagination controls
  const currentPage = externalPagination ? externalPagination.currentPage : internalCurrentPage;
  const pageSize = externalPagination ? externalPagination.pageSize : internalPageSize;

  // Calculate pagination info. In server mode `data`/`sortedItems` is just the current page,
  // so the caller passes totalItemsOverride (the server's `total`) instead.
  const totalItems = totalItemsOverride ?? sortedItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // Now apply pagination only after filtering and sorting. In server mode the page the caller
  // handed us via `data` IS the page to display — slicing it again would double-paginate.
  const paginatedData = pagination && totalItemsOverride === undefined
    ? sortedItems.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : sortedItems;

  const prevSortedItemsRef = useRef<T[]>([]);

  useEffect(() => {
    if (onDataChange && !deepEqual<T>(sortedItems, prevSortedItemsRef.current)) {
      onDataChange(sortedItems);
      prevSortedItemsRef.current = sortedItems;
    }
  }, [sortedItems, onDataChange]);

  // Reset to page 1 when filtered data changes
  useEffect(() => {
    // Reset to page 1 when filters change and data length changes
    if (!externalPagination) {
      setInternalCurrentPage(1);
    } else if (externalPagination && externalPagination.currentPage > totalPages && totalPages > 0) {
      // If using external pagination, inform parent that current page is out of bounds
      externalPagination.onPageChange(1);
    }
  }, [totalItems, externalPagination, totalPages]);

  // Handle page change function
  const handlePageChange = (page: number) => {
    if (externalPagination) {
      externalPagination.onPageChange(page);
    } else {
      setInternalCurrentPage(page);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (size: number) => {
    if (externalPagination) {
      externalPagination.onPageSizeChange(size);
      externalPagination.onPageChange(1); // Reset to first page when changing page size
    } else {
      setInternalPageSize(size);
      setInternalCurrentPage(1); // Reset to first page when changing page size
    }
  };

  const allRowsSelected = pagination
    ? paginatedData.every(selectedRows)
    : sortedItems.every(selectedRows);

  // Handle select all for current page only when pagination is enabled
  const handleSelectAll = (selected: boolean) => {
    if (onSelectAllRows) {
      onSelectAllRows(selected);
    }
  };

  const displayedItems = pagination ? paginatedData : sortedItems;

  // Navigate on a plain click, but NOT when the user has selected text in the row — otherwise
  // selecting a cell value to copy would fire the row's navigation. This makes table text
  // copyable while keeping click-to-open.
  const handleRowClick = (row: T, event: React.MouseEvent<HTMLTableRowElement>) => {
    if ((disableRow(row) && !getRowHref) || !onRowClick) return;
    const selection = typeof window !== 'undefined' ? window.getSelection?.() : null;
    if (selection && selection.toString().length > 0) return;
    onRowClick(row, event);
  };

  const handleRowAuxClick = (row: T, event: React.MouseEvent<HTMLTableRowElement>) => {
    if (event.button !== 1 || (disableRow(row) && !getRowHref)) return;
    const href = getRowHref?.(row);
    if (href) window.open(href, '_blank', 'noopener,noreferrer');
  };

  const getRowStyle = (row: T) => {
    if (disableRow(row) && !getRowHref)
      return 'bg-gray-100 opacity-50 cursor-not-allowed hover:bg-gray-50';
    if (selectedRows(row))
      return 'bg-blue-100 hover:bg-blue-200';
    return 'hover:bg-gray-200/50';
  }

  // If no visible columns are provided, show all columns
  const effectiveVisibleColumns: Record<string, boolean> = visibleColumns ||
    columns.reduce((acc, column) => ({ ...acc, [column.key]: true }), {});

  // Filter visible columns
  const visibleColumnsData = columns.filter(column =>
    effectiveVisibleColumns[column.key.toString()]
  );

  return (
    <div className={fillHeight ? 'lg:flex lg:h-full lg:min-h-0 lg:flex-col' : ''}>
      <FilterTags
        filterState={filterState}
        dateRangeFilters={dateRangeFilters}
        columns={columns}
        handleFilterChange={handleFilterChange}
        clearDateRangeFilter={clearDateRangeFilter}
        handleClearAllFilters={handleClearAllFilters}
        displayedItemsCount={sortedItems.length}
        totalItemsCount={data.length}
      />

      <div
        className={`relative ${
          fillHeight
            ? 'overflow-x-auto lg:overflow-auto lg:flex-1 lg:min-h-0'
            : 'overflow-x-auto min-h-[360px]'
        }`}
      >
        {/* In-place loading: dim the current rows and block interaction while refetching,
            instead of blank-swapping the table (only when rows are already shown). */}
        {loading && displayedItems.length > 0 && (
          <div className="pointer-events-none absolute inset-0 z-10 bg-white/50" />
        )}
        <table className={`min-w-full divide-y divide-gray-200 ${loading && displayedItems.length > 0 ? 'opacity-60 transition-opacity' : ''}`}>
          <thead>
            <tr>
              {selectableRows && (
                <th className="sticky top-0 z-20 px-6 py-3 text-left text-xs font-medium text-gray-500 bg-gray-100 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    className="form-checkbox h-5 w-5 text-blue-600 rounded"
                    checked={allRowsSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
              )}
              {visibleColumnsData.map((column) => (
                <th
                  key={String(column.key)}
                  className={`sticky top-0 z-20 ${headerPad} text-left text-xs font-medium text-gray-500 bg-gray-100 whitespace-nowrap`}
                >
                  <div className="flex items-center justify-between">
                    {column.filterable ? (
                      <button
                        onClick={() => {
                          setActiveFilter(activeFilter === String(column.key) ? null : String(column.key));
                        }}
                        className="text-left font-medium hover:text-blue-700 focus:outline-none flex items-center"
                      >
                        <span>{column.label}</span>
                        {(filterState[String(column.key)]?.length > 0 || dateRangeFilters[String(column.key)]) && (
                          <span className="ml-1 bg-blue-100 text-blue-600 text-xs px-1.5 py-0.5 rounded-full">
                            {isDateColumn(String(column.key), column.isDateColumn)
                              ? (dateRangeFilters[String(column.key)] ? '📅' : '')
                              : filterState[String(column.key)]?.length}
                          </span>
                        )}
                      </button>
                    ) : (
                      <span>{column.label}</span>
                    )}

                    {column.sortable && (
                      <button
                        onClick={() => {
                          if (serverSort) {
                            const nextDir =
                              serverSort.key === String(column.key) && serverSort.dir === 'asc'
                                ? 'desc'
                                : 'asc';
                            serverSort.onChange(String(column.key), nextDir);
                          } else {
                            handleSort(String(column.key));
                          }
                        }}
                        className={`p-1 rounded-md ${(serverSort ? serverSort.key : sortConfig.key) === column.key
                          ? 'bg-blue-100 text-blue-600'
                          : 'text-gray-400 hover:bg-gray-200 hover:text-gray-600'
                          }`}
                        title={`Sort ${column.label}`}
                      >
                        {(serverSort
                          ? serverSort.key === column.key && serverSort.dir === 'asc'
                          : sortConfig.key === column.key && sortConfig.direction === 'asc')
                          ? <ChevronUp size={14} />
                          : <ChevronDown size={14} />
                        }
                      </button>
                    )}

                    {column.filterable && activeFilter === String(column.key) && (
                      <FilterPopover<T>
                        column={column}
                        data={data}
                        filterState={filterState}
                        dateRangeFilters={dateRangeFilters}
                        filterPopoverRef={filterPopoverRef}
                        setActiveFilter={setActiveFilter}
                        handleFilterChange={handleFilterChange}
                        handleDateRangeFilterChange={handleDateRangeFilterChange}
                        clearDateRangeFilter={clearDateRangeFilter}
                        applyPresetDateRange={applyPresetDateRange}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayedItems.length === 0 ? (
              <tr>
                <td colSpan={visibleColumnsData.length + (selectableRows ? 1 : 0)} className="px-6 py-10 text-center text-gray-500">
                  {loading ? (
                    <span className="text-sm text-gray-400">กำลังโหลด...</span>
                  ) : (
                    emptyState ?? (
                      <div className="flex flex-col items-center">
                        <svg className="w-12 h-12 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-3-3v6m-7 4h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                        </svg>
                        <span>ไม่มีข้อมูลที่จะแสดง</span>
                      </div>
                    )
                  )}
                </td>
              </tr>
            ) : (
              displayedItems.map((row) => (
                disableRow(row) && DisabledRowComponent ? (
                  <DisabledRowComponent key={row.id} row={row} />
                ) : (
                  <tr
                    key={row.id}
                    data-cy={`table-row-${row.id}`}
                    className={`transition-colors duration-150 ${getRowStyle(row)}`}
                    onClick={(event) => handleRowClick(row, event)}
                    onAuxClick={(event) => handleRowAuxClick(row, event)}
                    role={getRowHref ? 'link' : undefined}
                    tabIndex={getRowHref ? 0 : undefined}
                    onKeyDown={(event) => {
                      if (getRowHref && (event.key === 'Enter' || event.key === ' ')) {
                        event.preventDefault();
                        window.location.assign(getRowHref(row));
                      }
                    }}
                  >
                    {selectableRows && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="form-checkbox h-5 w-5 text-blue-600 rounded"
                          checked={selectedRows(row)}
                          disabled={disableRow(row)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            e.stopPropagation();
                            if (!disableRow(row) && onRowSelect) {
                              onRowSelect(row);
                            }
                          }}
                        />
                      </td>
                    )}
                    {visibleColumnsData.map((column, columnIndex) => (
                      <td
                        key={`${row.id}-${String(column.key)}`}
                        className={`${cellPad} whitespace-nowrap`}
                      >
                        {getRowHref && columnIndex === 0 ? (
                          <a
                            href={getRowHref(row)}
                            className="block text-inherit no-underline"
                            onClick={(event) => event.stopPropagation()}
                          >
                            {column.render ? column.render(row) : String(row[column.key as keyof T])}
                          </a>
                        ) : (
                          column.render ? column.render(row) : String(row[column.key as keyof T])
                        )}
                      </td>
                    ))}
                  </tr>
                )
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination && (
        <div className="mt-4 mx-4">
          <PaginationControls
            currentPage={currentPage}
            pageSize={pageSize}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={pageSizeOptions}
          />
        </div>
      )}
    </div>
  );
};

export default TableComponent;
